/**
 * Formulaire d'inscription partenaire : validation client + messages serveur (redirect).
 *
 * 1) Validation client : listes <ul class="errors"> sous chaque .form-group (messages i18n data-err-*).
 * 2) Schéma Fusion / PHP : erreurs serveur identiques après rechargement du formulaire.
 * 3) Règles métier JS : concept « none », NPA suisse, mots de passe.
 */
const CLIENT_ERROR_ATTR = 'data-partner-registration-client-error';

function partnerRegistrationErrorCodeToDatasetKey(errorCode) {
  if (errorCode == null || errorCode === '' || !String(errorCode).startsWith('error.')) {
    return null;
  }
  const parts = String(errorCode)
    .slice(6)
    .split('.');
  if (parts.length < 1) {
    return null;
  }
  return (
    'err' + parts.map((p) => p.charAt(0).toUpperCase() + p.slice(1)).join('')
  );
}

function trimValue(value) {
  return String(value ?? '').trim();
}

/**
 * Retourne le conteneur de champ (FieldContainer Neos).
 */
function getFieldGroup(element) {
  return element?.closest('.form-group, .tc-partner-registration-form__field');
}

/**
 * Supprime les erreurs client injectées avant une nouvelle validation.
 */
function clearClientErrors(formRoot) {
  formRoot.querySelectorAll(`ul.errors[${CLIENT_ERROR_ATTR}]`).forEach((element) => {
    element.remove();
  });
  formRoot.querySelectorAll('.form-group.error, .tc-partner-registration-form__field.error').forEach((element) => {
    element.classList.remove('error');
  });
}

/**
 * Affiche un message sous le champ, comme le ErrorRenderer Neos (<ul class="errors"><li>…</li></ul>).
 */
function showFieldError(fieldGroup, message) {
  if (!fieldGroup || !message) {
    return;
  }

  fieldGroup.classList.add('error');

  let errorList = fieldGroup.querySelector(`ul.errors[${CLIENT_ERROR_ATTR}]`);
  if (!errorList) {
    errorList = document.createElement('ul');
    errorList.className = 'errors';
    errorList.setAttribute(CLIENT_ERROR_ATTR, 'true');
    fieldGroup.appendChild(errorList);
  }

  errorList.replaceChildren();
  const item = document.createElement('li');
  item.textContent = message;
  errorList.appendChild(item);
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidSwissZipCode(value) {
  const zip = trimValue(value);
  if (!/^\d{4}$/.test(zip)) {
    return false;
  }
  const numeric = Number.parseInt(zip, 10);
  return numeric >= 1000 && numeric <= 9658;
}

/**
 * Fait défiler la page vers le premier champ en erreur (client ou serveur), en tenant compte du header fixe.
 */
function scrollToFirstFormError(formRoot) {
  const form = formRoot.closest('form');
  if (!form) {
    return;
  }

  const firstErrorItem = formRoot.querySelector('.form-group ul.errors li, .tc-partner-registration-form__field ul.errors li');
  if (!firstErrorItem) {
    return;
  }

  const target = firstErrorItem.closest('.form-group, .tc-partner-registration-form__field')
    || firstErrorItem;

  window.requestAnimationFrame(() => {
    const headerOffset = Number.parseInt(
      getComputedStyle(document.documentElement).getPropertyValue('--header-height') || '90',
      10
    );
    const rect = target.getBoundingClientRect();
    const scrollTop = window.scrollY + rect.top - headerOffset - 16;

    window.scrollTo({
      top: Math.max(0, scrollTop),
      behavior: 'smooth',
    });

    const focusable = target.querySelector('input:not([type="hidden"]), select, textarea');
    if (focusable) {
      focusable.focus({ preventScroll: true });
    }
  });
}

/**
 * Construit la liste des erreurs client à afficher (tous les champs invalides).
 */
function collectClientValidationErrors(form, formRoot, context) {
  const errors = [];
  const msg = (datasetKey, fallbackKey) => formRoot.dataset[datasetKey] || formRoot.dataset[fallbackKey] || formRoot.dataset.errGeneric || '';

  const push = (element, message) => {
    if (!element || !message) {
      return;
    }
    errors.push({
      element,
      message,
      group: getFieldGroup(element),
    });
  };

  const requireText = (selector, datasetKey) => {
    const input = form.querySelector(selector);
    if (input && trimValue(input.value) === '') {
      push(input, msg(datasetKey));
    }
  };

  const workshopConcept = context.conceptInput ? context.conceptInput.value : '';
  const isExplicitNone = workshopConcept === 'none';
  const hasRegistrationConcept = workshopConcept !== '' && !isExplicitNone;

  requireText('[name$="[companyName]"]', 'errCompanyNameRequired');

  if (context.conceptInput) {
    if (workshopConcept === '') {
      push(context.conceptInput, msg('errWorkshopConceptInvalid'));
    } else if (isExplicitNone) {
      push(context.conceptInput, msg('errWorkshopConceptNoneNotAccepted'));
    }
  }

  requireText('[name$="[streetAddress]"]', 'errStreetAddressRequired');
  requireText('[name$="[city]"]', 'errCityRequired');
  requireText('[name$="[firstName]"]', 'errFirstNameRequired');
  requireText('[name$="[lastName]"]', 'errLastNameRequired');

  if (context.zipCodeInput) {
    const zip = trimValue(context.zipCodeInput.value);
    if (zip === '') {
      push(context.zipCodeInput, msg('errZipCodeRequired'));
    } else if (!isValidSwissZipCode(zip)) {
      push(context.zipCodeInput, msg('errZipCodeInvalid'));
    }
  }

  const cantonInput = form.querySelector('[name$="[canton]"]');
  if (cantonInput && trimValue(cantonInput.value) === '') {
    push(cantonInput, msg('errCantonRequired'));
  }

  const emailInput = form.querySelector('[name$="[email]"]');
  if (emailInput) {
    const email = trimValue(emailInput.value);
    if (email === '') {
      push(emailInput, msg('errEmailRequired'));
    } else if (!isValidEmail(email)) {
      push(emailInput, msg('errEmailInvalid'));
    }
  }

  if (context.phoneInput) {
    if (trimValue(context.phoneInput.value) === '') {
      push(context.phoneInput, msg('errPhoneRequired'));
    }
  }

  if (context.acceptTermsInput && !context.acceptTermsInput.checked) {
    push(context.acceptTermsInput, msg('errAcceptTermsRequired'));
  }

  if (hasRegistrationConcept) {
    if (context.passwordInput) {
      const password = context.passwordInput.value;
      if (password === '') {
        push(context.passwordInput, msg('errPasswordRequired'));
      } else if (password.length < context.minPasswordLength) {
        push(context.passwordInput, msg('errPasswordTooShort'));
      }
    }

    if (
      context.passwordInput
      && context.passwordConfirmationInput
      && context.passwordInput.value !== context.passwordConfirmationInput.value
    ) {
      push(
        context.passwordConfirmationInput,
        context.submitButton?.dataset.passwordMismatch || msg('errPasswordMismatch')
      );
    }
  }

  return errors;
}

const partnerRegistration = {
  init() {
    const forms = document.querySelectorAll('[data-partner-registration-form="true"]');
    if (!forms.length) {
      return;
    }

    forms.forEach((formRoot) => {
      const form = formRoot.closest('form');
      if (!form) {
        return;
      }

      const conceptInput = form.querySelector('[name$="[workshopConcept]"]');
      const derendingerWrapper = form.querySelector('.tc-partner-registration-form__derendinger');
      const noConceptBox = form.querySelector('.tc-partner-registration-form__no-concept');
      const passwordCard = form.querySelector('.tc-partner-registration-form__password-card');
      const passwordInput = form.querySelector('[name$="[password]"]');
      const passwordConfirmationInput = form.querySelector('[name$="[passwordConfirmation]"]');
      const acceptTermsInput = form.querySelector('input[type="checkbox"][name$="[acceptTerms]"]');
      const zipCodeInput = form.querySelector('[name$="[zipCode]"]');
      const phoneInput = form.querySelector('[name$="[phone]"]');
      const submitButton = form.querySelector('.tc-partner-registration-form__submit');
      const submitText = form.querySelector('.tc-partner-registration-form__submit-text');
      const contactLink = form.querySelector('[data-partner-registration-contact]');
      const successBox = form.querySelector('[data-partner-registration-success]');
      const errorBox = form.querySelector('[data-partner-registration-error]');
      const errorText = form.querySelector('[data-partner-registration-error-message]');
      const query = new URLSearchParams(window.location.search);
      const minPasswordLength = parseInt(formRoot.dataset.minPasswordLength || '8', 10);

      const validationContext = {
        conceptInput,
        zipCodeInput,
        phoneInput,
        acceptTermsInput,
        passwordInput,
        passwordConfirmationInput,
        submitButton,
        minPasswordLength,
      };

      const getGenericErrorMessage = () => formRoot.dataset.errGeneric || '';
      const getServerRedirectErrorMessage = (code) => {
        if (code === 'error.submit.generic' || !code) {
          return getGenericErrorMessage();
        }
        const dkey = partnerRegistrationErrorCodeToDatasetKey(code);
        if (dkey && formRoot.dataset[dkey]) {
          return formRoot.dataset[dkey];
        }
        return getGenericErrorMessage();
      };

      const setErrorMessage = (message) => {
        if (!errorBox || !errorText) {
          return;
        }
        errorText.textContent = message || getGenericErrorMessage();
        errorBox.classList.remove('is-hidden');
      };

      const hideErrorMessage = () => {
        if (!errorBox) {
          return;
        }
        errorBox.classList.add('is-hidden');
      };

      const hideSuccessMessage = () => {
        if (!successBox) {
          return;
        }
        successBox.classList.add('is-hidden');
      };

      if (query.get('partnerRegistrationSuccess') === '1' && query.get('partnerRegistrationError') !== '1' && successBox) {
        successBox.classList.remove('is-hidden');
      }

      if (query.get('partnerRegistrationError') === '1') {
        const errorCode = query.get('partnerRegistrationErrorCode') || '';
        setErrorMessage(getServerRedirectErrorMessage(errorCode));
      }

      if (formRoot.querySelector('.form-group ul.errors li, .tc-partner-registration-form__field ul.errors li')) {
        scrollToFirstFormError(formRoot);
      }

      const syncConceptState = () => {
        if (!conceptInput) {
          return;
        }

        const workshopConcept = conceptInput.value;
        const isExplicitNone = workshopConcept === 'none';
        const hasRegistrationConcept = workshopConcept !== '' && !isExplicitNone;

        if (derendingerWrapper) {
          derendingerWrapper.classList.toggle('is-hidden', !hasRegistrationConcept);
          const derendingerInput = derendingerWrapper.querySelector('input');
          if (derendingerInput) {
            derendingerInput.required = false;
            if (!hasRegistrationConcept) {
              derendingerInput.value = '';
            }
          }
        }

        if (passwordCard) {
          passwordCard.classList.toggle('is-hidden', !hasRegistrationConcept);
        }

        if (noConceptBox) {
          noConceptBox.classList.toggle('is-hidden', !isExplicitNone || hasRegistrationConcept);
        }

        if (passwordInput) {
          if (!hasRegistrationConcept) {
            passwordInput.value = '';
          }
        }

        if (passwordConfirmationInput) {
          if (!hasRegistrationConcept) {
            passwordConfirmationInput.value = '';
          }
        }

        if (submitButton) {
          submitButton.classList.toggle('is-hidden', isExplicitNone);
        }

        if (submitText && submitButton) {
          submitText.textContent = submitButton.dataset.labelSubmit || submitText.textContent;
        }

        if (contactLink) {
          contactLink.classList.toggle('is-hidden', !isExplicitNone);
        }
      };

      if (conceptInput) {
        conceptInput.addEventListener('change', syncConceptState);
      }

      form.addEventListener('submit', (event) => {
        hideSuccessMessage();
        clearClientErrors(formRoot);

        const clientErrors = collectClientValidationErrors(form, formRoot, validationContext);
        if (clientErrors.length > 0) {
          event.preventDefault();
          hideErrorMessage();

          clientErrors.forEach(({ group, message }) => {
            showFieldError(group, message);
          });

          scrollToFirstFormError(formRoot);

          return;
        }

        hideErrorMessage();
      });

      form.querySelectorAll('input, select, textarea').forEach((input) => {
        const eventName = input.type === 'checkbox' || input.tagName === 'SELECT' ? 'change' : 'input';
        input.addEventListener(eventName, () => {
          const group = getFieldGroup(input);
          if (group) {
            group.classList.remove('error');
            group.querySelectorAll(`ul.errors[${CLIENT_ERROR_ATTR}]`).forEach((element) => {
              element.remove();
            });
          }
        });
      });

      if (contactLink) {
        contactLink.addEventListener('click', (event) => {
          const href = contactLink.getAttribute('href');
          if (href === '#' || href === '') {
            event.preventDefault();
          }
        });
      }

      syncConceptState();
    });
  },
};

export default partnerRegistration;
